// ---------------------------------------------------------------------
// scheduledItinerary — turns a Scheduled/In Progress plan into an ordered
// list of appointments (the booked roadmap), each with its visits, date,
// duration, and done/pending progress. The ScheduledPlanItinerary view
// renders this instead of the editable planning board once a plan is
// booked. Pure + testable; no React.
// ---------------------------------------------------------------------

function phaseOf(item, visit) {
  const n = Number(visit?.phase_number ?? item?.phase_number);
  return Number.isFinite(n) && n >= 1 ? n : 0;
}

function earliest(values) {
  const times = values
    .filter(Boolean)
    .map((v) => new Date(v).getTime())
    .filter((t) => Number.isFinite(t));
  if (times.length === 0) return null;
  return new Date(Math.min(...times)).toISOString();
}

/**
 * @returns {Array<{
 *   phase: number,
 *   entries: Array<{item, visit}>,
 *   start: string|null, end: string|null, durationMin: number|null,
 *   total: number, completed: number, allDone: boolean
 * }>}  sorted by phase (appointment order).
 */
export function buildScheduledItinerary(plan) {
  const items = plan?.items ?? [];
  const byPhase = new Map();
  for (const item of items) {
    if (item?.status === "Declined" || item?.status === "Cancelled") continue;
    const visits = Array.isArray(item?.procedure_visits) ? item.procedure_visits : [];
    for (const visit of visits) {
      const phase = phaseOf(item, visit);
      if (phase < 1) continue; // unbooked / needs-appointment work isn't part of the itinerary
      if (!byPhase.has(phase)) byPhase.set(phase, []);
      byPhase.get(phase).push({ item, visit });
    }
  }

  return [...byPhase.keys()]
    .sort((a, b) => a - b)
    .map((phase) => {
      const entries = byPhase.get(phase).sort(
        (a, b) => (Number(a.visit?.sequence) || 0) - (Number(b.visit?.sequence) || 0),
      );
      const start = earliest(entries.map((e) => e.visit?.appointment_scheduled_start));
      const end = earliest(entries.map((e) => e.visit?.appointment_scheduled_end));
      const durations = entries
        .map((e) => Number(e.visit?.appointment_duration_min))
        .filter((n) => Number.isFinite(n) && n > 0);
      const completed = entries.filter((e) => e.visit?.completed_at).length;
      return {
        phase,
        entries,
        start,
        end,
        durationMin: durations.length ? Math.max(...durations) : null,
        total: entries.length,
        completed,
        allDone: entries.length > 0 && completed === entries.length,
      };
    });
}

// Same-calendar-day comparison in the given timezone, using the locale
// date string so it works without pulling in a date library.
export function isSameClinicDay(iso, now, timeZone) {
  if (!iso || !now) return false;
  const opts = timeZone ? { timeZone } : undefined;
  try {
    return new Date(iso).toLocaleDateString("en-CA", opts)
      === new Date(now).toLocaleDateString("en-CA", opts);
  } catch {
    return false;
  }
}

/** Status of one appointment relative to "now": done | today | upcoming | overdue. */
export function appointmentStatus(appt, now, timeZone) {
  if (appt.allDone) return "done";
  if (appt.start && isSameClinicDay(appt.start, now, timeZone)) return "today";
  if (appt.start && new Date(appt.start).getTime() < new Date(now).getTime()) return "overdue";
  return "upcoming";
}
